import CircularProgress from "@material-ui/core/CircularProgress";
import MuiButton from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import CreateCustomDomainListModal from "../../CampaignCreationCustomSlides/PublicationListSelectionSlide/components/CreateCustomDomainListModal/CreateCustomDomainListModal";
import EditCustomDomainListModal from "../../CampaignCreationCustomSlides/PublicationListSelectionSlide/components/EditCustomDomainListModal/EditCustomDomainListModal";
import PublicationListsTable from "./components/PublicationListsTable";
import { BrandgenPublicationList, CAMPAIGN_STEPS } from "../../../types/campaignTypes";
import { ManageLink } from "../../../components/redesign/elements/ManageLink/ManageLink";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { basicSteps } from "../../../constants/steps";
import {
  createDomainList,
  getCustomDomainLists,
  getPublicDomainLists,
  updateDomainList,
} from "../../CampaignCreationCustomSlides/PublicationListSelectionSlide/publicationListApi";
import {
  setCustomDomainLists,
  setPublicDomainLists,
  setSelectedDomainIds as setSelectedDomainIdsAction,
  setSelectedDomainListsIds as setSelectedDomainListsIdsAction,
  setSelectedDomainNames as setSelectedDomainNamesAction,
} from "../../../redux/reducers/campaignToCreate";
import { useGlobalSelectedAdvertiser } from "../../../hooks/useGlobalSelectedAdvertiser";

// ─── Helpers ────────────────────────────────────────────────────────────────

const isOnlyPublicationListsStep = (steps: CAMPAIGN_STEPS[] | null | undefined): boolean => {
  if (!steps?.length) return false;
  const nonBaseSteps = steps.filter((step) => !basicSteps.includes(step));
  return nonBaseSteps.length === 1 && nonBaseSteps[0] === CAMPAIGN_STEPS.PUBLICATION_LISTS;
};

// ─── Styled components ───────────────────────────────────────────────────────

const PageRoot = styled("div")({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const PanelsRow = styled("div")({
  display: "flex",
  gap: 16,
  flex: 1,
  minHeight: 0,
});

const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));

const CardHeader = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 18px",
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const CardTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const CardBody = styled("div")({
  flex: 1,
  overflowY: "auto",
});

const CenteredMessage = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 40,
  color: theme.palette.brand.gray400,
}));

const PlaceholderText = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray400,
}));

const CreateButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 600,
  padding: "6px 14px",
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  minWidth: "unset",
  "&:hover": {
    background: theme.palette.brand.coralHover,
    transform: "translateY(-1px)",
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
  },
}));

// ─── Component ───────────────────────────────────────────────────────────────

type PublicationListsViewProps = {
  panelMode?: boolean;
};

const PublicationListsView: FC<PublicationListsViewProps> = ({ panelMode = false }) => {
  const dispatch = useDispatch();
  const { brandgenAdvertiserId } = useGlobalSelectedAdvertiser();

  const publicLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.publicDomainLists);
  const customLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.customDomainLists);
  const campaignSteps = useSelector((state: ReduxApplicationState) => state.campaignToCreate.campaignSteps);
  const selectedDomainNamesFromStore = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedDomainNames,
  );
  const selectedDomainListsIdsFromStore = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedDomainListsIds,
  );
  const selectedDomainIdsFromStore = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedDomainIds,
  );

  // ─── Local state ──────────────────────────────────────────────────────────
  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>(selectedDomainIdsFromStore ?? []);
  const [selectedDomainNames, setSelectedDomainNames] = useState<string[]>(selectedDomainNamesFromStore ?? []);
  const [selectedDomainListsIds, setSelectedDomainListsIds] = useState<string[]>(selectedDomainListsIdsFromStore ?? []);
  const [isLoadingPublic, setIsLoadingPublic] = useState(false);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const selectedAdvertiserId = brandgenAdvertiserId ?? "";
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [listToEdit, setListToEdit] = useState<BrandgenPublicationList | null>(null);

  // ─── Derived ──────────────────────────────────────────────────────────────
  const isSplitMode = useMemo(() => isOnlyPublicationListsStep(campaignSteps), [campaignSteps]);
  const effectiveSplitMode = panelMode ? false : isSplitMode;
  const allLists = useMemo(() => [...publicLists, ...customLists], [publicLists, customLists]);

  // ─── Sync selection arrays from store (e.g. template restore) ────────────
  useEffect(() => {
    setSelectedDomainListsIds(selectedDomainListsIdsFromStore ?? []);
    setSelectedDomainIds(selectedDomainIdsFromStore ?? []);
    setSelectedDomainNames(selectedDomainNamesFromStore ?? []);
  }, [selectedDomainListsIdsFromStore, selectedDomainIdsFromStore, selectedDomainNamesFromStore]);

  // ─── Derive list-level selection from domain IDs (template restore) ───────
  useEffect(() => {
    if (allLists.length === 0 || selectedDomainIds.length === 0 || selectedDomainListsIds.length > 0) return;
    const listIdsFromDomains = new Set<string>();
    for (const list of allLists) {
      const domainIdsInList = new Set((list.listDomains ?? []).map((d) => d.id));
      if (selectedDomainIds.some((id) => domainIdsInList.has(id))) {
        listIdsFromDomains.add(list.id);
      }
    }
    if (listIdsFromDomains.size > 0) {
      setSelectedDomainListsIds((prev) => [...new Set([...prev, ...listIdsFromDomains])]);
    }
  }, [allLists, selectedDomainIds, selectedDomainListsIds.length]);

  // ─── Panel mode: keep Redux in sync as user checks/unchecks ─────────────
  useEffect(() => {
    if (!panelMode) return;
    dispatch(setSelectedDomainListsIdsAction(selectedDomainListsIds));
    dispatch(setSelectedDomainNamesAction(selectedDomainNames));
    dispatch(setSelectedDomainIdsAction(selectedDomainIds));
  }, [panelMode, dispatch, selectedDomainListsIds, selectedDomainNames, selectedDomainIds]);

  // ─── Fetch public lists on mount ─────────────────────────────────────────
  useEffect(() => {
    if (publicLists.length > 0) return;
    const load = async () => {
      try {
        setIsLoadingPublic(true);
        const lists = await getPublicDomainLists();
        dispatch(setPublicDomainLists(lists));
      } catch (error) {
        console.error("fetchPublicDomainLists:", error);
      } finally {
        setIsLoadingPublic(false);
      }
    };
    load();
  }, [dispatch, publicLists.length]);

  // ─── Fetch custom lists when advertiser changes ───────────────────────────
  const fetchCustom = useCallback(
    async (advertiserId: string) => {
      if (!advertiserId) return;
      setIsLoadingCustom(true);
      try {
        const lists = await getCustomDomainLists(advertiserId);
        dispatch(setCustomDomainLists(lists ?? []));
      } catch (error) {
        console.error("fetchCustomDomainLists:", error);
        dispatch(setCustomDomainLists([]));
      } finally {
        setIsLoadingCustom(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!selectedAdvertiserId) return;
    fetchCustom(selectedAdvertiserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdvertiserId]);

  // ─── Selection handlers ───────────────────────────────────────────────────
  const toggleSelectDomainLists = useCallback(
    (toggledListId: string) => {
      const list = allLists.find((l) => l.id === toggledListId);
      const activeDomains = list?.listDomains?.filter((d) => d.active) ?? [];

      if (selectedDomainListsIds.includes(toggledListId)) {
        setSelectedDomainListsIds((ids) => ids.filter((id) => id !== toggledListId));
        setSelectedDomainNames((names) => names.filter((n) => !activeDomains.some((d) => d.name === n)));
        setSelectedDomainIds((ids) => ids.filter((id) => !activeDomains.some((d) => d.id === id)));
      } else {
        setSelectedDomainListsIds((ids) => [...ids, toggledListId]);
        setSelectedDomainIds((ids) => [...new Set([...ids, ...activeDomains.map((d) => d.id)])]);
        setSelectedDomainNames((names) => [...new Set([...names, ...activeDomains.map((d) => d.name)])]);
      }
    },
    [allLists, selectedDomainListsIds],
  );

  const toggleSelectDomain = useCallback(
    (domainId: string) => {
      const domain = allLists.flatMap((l) => l.listDomains ?? []).find((d) => d.id === domainId);
      const name = domain?.name;
      const isRemoving = selectedDomainIds.includes(domainId);

      setSelectedDomainIds((ids) => (isRemoving ? ids.filter((id) => id !== domainId) : [...ids, domainId]));
      if (name) {
        setSelectedDomainNames((names) =>
          isRemoving ? names.filter((n) => n !== name) : [...new Set([...names, name])],
        );
      }
    },
    [allLists, selectedDomainIds],
  );

  // ─── Management mode handlers ─────────────────────────────────────────────
  const handleEditClick = (list: BrandgenPublicationList) => {
    setListToEdit(list);
    setIsEditModalOpen(true);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <PageRoot>
      {panelMode && (
        <ManageLink>
          To create or manage publication lists, go to{" "}
          <a href="/publication-lists" target="_blank" rel="noopener noreferrer">
            Publication List ↗
          </a>
          .
        </ManageLink>
      )}
      <PanelsRow>
        {/* ── Public Lists ── */}
        <Card>
          <CardHeader>
            <CardTitle>BrandGen Publication Lists</CardTitle>
          </CardHeader>
          <CardBody>
            {isLoadingPublic ? (
              <CenteredMessage>
                <CircularProgress size={28} />
              </CenteredMessage>
            ) : (
              <PublicationListsTable
                lists={publicLists}
                variant="public"
                isSelectionMode={panelMode}
                isSplitMode={effectiveSplitMode}
                panelMode={panelMode}
                selectedListIds={selectedDomainListsIds}
                selectedDomainIds={selectedDomainIds}
                onListSelect={toggleSelectDomainLists}
                onDomainSelect={toggleSelectDomain}
              />
            )}
          </CardBody>
        </Card>

        {/* ── Custom Lists ── */}
        <Card>
          <CardHeader>
            <CardTitle>Custom Publication Lists</CardTitle>
            {/* Create button: hidden in stepper mode, split mode, and panel mode */}
            {!isSplitMode && !panelMode && (
              <CreateButton disableElevation onClick={() => setIsCreateModalOpen(true)}>
                Create List
              </CreateButton>
            )}
          </CardHeader>
          <CardBody>
            {isLoadingCustom ? (
              <CenteredMessage>
                <CircularProgress size={28} />
              </CenteredMessage>
            ) : !panelMode && !selectedAdvertiserId ? (
              <CenteredMessage>
                <PlaceholderText>Select an advertiser from the profile menu to view custom lists.</PlaceholderText>
              </CenteredMessage>
            ) : (
              <PublicationListsTable
                lists={customLists}
                variant="custom"
                isSelectionMode={panelMode}
                isSplitMode={effectiveSplitMode}
                panelMode={panelMode}
                selectedListIds={selectedDomainListsIds}
                selectedDomainIds={selectedDomainIds}
                onListSelect={toggleSelectDomainLists}
                onDomainSelect={toggleSelectDomain}
                onEditClick={panelMode ? undefined : handleEditClick}
              />
            )}
          </CardBody>
        </Card>
      </PanelsRow>

      {/* ── Modals ── */}
      <CreateCustomDomainListModal
        isOpen={isCreateModalOpen}
        handleClose={() => setIsCreateModalOpen(false)}
        createDomainList={createDomainList}
      />
      <EditCustomDomainListModal
        isOpen={isEditModalOpen}
        handleClose={() => {
          setIsEditModalOpen(false);
          setListToEdit(null);
        }}
        customDomainListToEdit={listToEdit}
        updateDomainList={updateDomainList}
      />
    </PageRoot>
  );
};

export default PublicationListsView;
